/**
 * ABD (páginas 01): the Área de entrega page.
 *
 * One field, one question, and an answer that offers something either way. A
 * visitor types eight digits and learns whether a Ciclista reaches them, and
 * where to collect when one does not.
 *
 * **The verdict came back on 11 September 2026.** It had been missing since the
 * scheduling app that used to answer was removed: `/cart/shipping_rates.json`
 * prices the cart it is asked about, and a visitor arriving from the footer has
 * no cart, so it answered nothing for São Paulo exactly as it did for Rio, and
 * every CEP got `data-undecided`. Carrinho ticket 24 held the decision open and
 * ticket 39 made it. Pickeasy holds the 21 prefixes (ADR 0010) and its app proxy
 * answers a request with no cart, so the page asks the app the same question the
 * app's own widget asks, through `abd-cep.js`.
 *
 * Nothing here holds a list of CEPs, a Zona or a price, which is ADR 0006 and
 * ADR 0009's rule and the reason the answer follows Marina when she widens a Zona
 * in an admin screen nobody here can see.
 *
 * **There is no price on this page any more, and there was none before.** The
 * sentence that quoted one needed `/cart/shipping_rates.json`, which needs a cart
 * and, under ADR 0010, a delivery slot committed at checkout. Nobody arriving
 * here has either. So the money line is `data-price-later`, which says the value
 * appears in the cart, and that is what the cart does.
 *
 * No string a customer reads is in this file. They are all `data-*` attributes on
 * `sections/abd-area.liquid`.
 */

import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { CEP_DEBOUNCE_MS, CEP_DIGITS, area, cepDigits, formatCep } from '@theme/abd-cep';

/**
 * @typedef {object} AreaRefs
 * @property {HTMLElement} [field] - The label and the input, `hidden` until this runs.
 * @property {HTMLInputElement} [cep] - The eight digits everything follows from.
 * @property {HTMLElement} [answer] - The live region the whole answer is written into.
 * @property {HTMLElement} [cta] - The way on to the arranjos, shown only to a CEP we reach.
 */

/**
 * The Área de entrega field.
 *
 * @extends Component<AreaRefs>
 */
class AbdArea extends Component {
  /** The CEP on screen, eight digits with no punctuation. */
  #cep = '';

  connectedCallback() {
    super.connectedCallback();

    // The field is `hidden` in the Liquid and revealed here, so a browser that
    // never runs this file shows the promise, the Retirada and its address
    // rather than a box that swallows eight digits and answers nothing.
    if (this.refs.field) this.refs.field.hidden = false;

    this.addEventListener('input', this.#onInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#askSoon.cancel();
  }

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target !== this.refs.cep) return;

    const input = this.refs.cep;
    if (!input) return;

    const digits = cepDigits(input.value);
    if (digits === this.#cep) return;

    this.#cep = digits;
    if (this.refs.cta) this.refs.cta.hidden = true;

    if (digits.length < CEP_DIGITS) {
      // Silent until they have typed something, so the count does not scold
      // somebody who has typed one digit of eight.
      this.#say(digits.length > 0 ? [this.#copy('short')] : []);
      this.#askSoon.cancel();
      return;
    }

    // Written out the way a Brazilian reads it, now that there is enough to
    // punctuate. Only while the caret is at the end, though: inserting the
    // hyphen moves the caret there, and somebody correcting the second digit of
    // a CEP they already typed would be thrown to the end of the field on every
    // keystroke.
    if (input.selectionStart === input.value.length) input.value = formatCep(digits);

    this.#say([this.#copy('searching')]);
    this.#askSoon();
  };

  #askSoon = debounce(() => this.#ask(), CEP_DEBOUNCE_MS);

  /**
   * Asks Pickeasy whether a Ciclista reaches the CEP on screen.
   *
   * Three answers and they are three different things. Inside gets the promise
   * and the way on to the arranjos. Outside gets the Retirada, warmly, and never
   * a bicycle it will not see. And an app that could not be asked gets neither:
   * `undefined` is not a refusal, and reading it as one would close the área to
   * the whole city over somebody else's outage.
   */
  async #ask() {
    const cep = this.#cep;
    if (cep.length !== CEP_DIGITS) return;

    const inside = await area.lookup(this.dataset.pickeasyUrl, this.dataset.shop, cep);

    // A newer CEP is on screen and this answer is about an address nobody is on
    // any more. There is no abort to do: the download is shared by every CEP the
    // page asks about, so one stale answer is dropped rather than cancelled.
    if (this.#cep !== cep) return;

    if (inside === undefined) {
      this.#say([this.#copy('undecided')]);
      return;
    }

    if (inside) {
      this.#say([this.#copy('inside'), this.#copy('priceLater')]);
      if (this.refs.cta) this.refs.cta.hidden = false;
      return;
    }

    this.#say([this.#copy('outside')]);
  }

  /**
   * One of the section's sentences.
   *
   * @param {string} name - The `data-*` attribute, camel-cased.
   * @returns {string}
   */
  #copy(name) {
    return this.dataset[name] ?? '';
  }

  /**
   * Prints the answer, one paragraph per line.
   *
   * The region is emptied and refilled rather than appended to, so an answer is
   * never half of the last one, and it is left in the tree while empty so a
   * screen reader has something to announce into.
   *
   * @param {string[]} lines
   */
  #say(lines) {
    const answer = this.refs.answer;
    if (!answer) return;

    answer.replaceChildren(...lines.filter((line) => line !== '').map((line) => this.#paragraph(line)));
  }

  /**
   * @param {string} line
   * @returns {HTMLParagraphElement}
   */
  #paragraph(line) {
    const paragraph = document.createElement('p');
    paragraph.textContent = line;

    return paragraph;
  }
}

if (!customElements.get('abd-area')) {
  customElements.define('abd-area', AbdArea);
}
